import * as React from 'react';
import { Title, useTranslate, useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { Box, Tab, Tabs } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import CloudIcon from '@mui/icons-material/Cloud';
import StorageIcon from '@mui/icons-material/Storage';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import KeyIcon from '@mui/icons-material/Key';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import ScienceIcon from '@mui/icons-material/Science';
import TuneIcon from '@mui/icons-material/Tune';
import EcsServicesPanel from './EcsServicesPanel';
import RdsClusterPanel from './RdsClusterPanel';
import RdsMetricsPanel from './RdsMetricsPanel';
import ValkeyList from '../valkey/ValkeyList';
import MaterialstorageList from '../materialstorage/MaterialstorageList';
import SecretsList from '../secrets/SecretsList';
import JwtKeyList from './JwtKeyList';
import OAuthClientList from './OAuthClientList';
import ApiConfigList from './ApiConfigList';
import TestScenarioPanel from './TestScenarioPanel';
import SettingsPanel from './SettingsPanel';

const TEST_USERS_ENABLED = process.env.REACT_APP_TEST_USERS_ENABLED === 'true';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
    <div role="tabpanel" hidden={value !== index} style={{ width: '100%' }}>
        {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
);

const HallintaDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = parseInt(searchParams.get('tab') || '0', 10);
    const setTab = (value: number) =>
        setSearchParams({ tab: String(value) }, { replace: true });
    const translate = useTranslate();
    const dataProvider = useDataProvider();

    const { data: rdsData } = useQuery({
        queryKey: ['admin/rds-status'],
        queryFn: () => dataProvider.getAny('admin/rds-status', ''),
        refetchInterval: 60000,
        staleTime: 30000,
    });

    const rdsClusters = rdsData?.data || [];

    return (
        <Box sx={{ width: '100%' }}>
            <Title title={translate('menu.hallinta')} />
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab
                    icon={<CloudIcon />}
                    iconPosition="start"
                    label={translate('hallinta.tabs.aws_overview')}
                />
                <Tab
                    icon={<StorageIcon />}
                    iconPosition="start"
                    label={translate('resources.valkey.name', {
                        smart_count: 2,
                    })}
                />
                <Tab
                    icon={<WarehouseIcon />}
                    iconPosition="start"
                    label={translate('resources.materialstorage.name', {
                        smart_count: 2,
                    })}
                />
                <Tab
                    icon={<KeyIcon />}
                    iconPosition="start"
                    label={translate('hallinta.tabs.secrets')}
                />
                <Tab
                    icon={<VpnKeyIcon />}
                    iconPosition="start"
                    label={translate('hallinta.tabs.jwt_keys')}
                />
                <Tab
                    icon={<LockOpenIcon />}
                    iconPosition="start"
                    label={translate('hallinta.tabs.oauth_clients')}
                />
                <Tab
                    icon={<SettingsApplicationsIcon />}
                    iconPosition="start"
                    label={translate('hallinta.tabs.api_configs')}
                />
                <Tab
                    icon={<TuneIcon />}
                    iconPosition="start"
                    label={translate('hallinta.tabs.settings')}
                />
                {TEST_USERS_ENABLED && (
                    <Tab
                        icon={<ScienceIcon />}
                        iconPosition="start"
                        label={translate('hallinta.tabs.tests')}
                    />
                )}
            </Tabs>
            <TabPanel value={tab} index={0}>
                <EcsServicesPanel />
                <Box sx={{ mt: 3 }} />
                <RdsClusterPanel clusters={rdsClusters} />
                <Box sx={{ mt: 3 }} />
                <RdsMetricsPanel clusters={rdsClusters} />
            </TabPanel>
            <TabPanel value={tab} index={1}>
                <ValkeyList resource="valkey" />
            </TabPanel>
            <TabPanel value={tab} index={2}>
                <MaterialstorageList resource="materialstorage" />
            </TabPanel>
            <TabPanel value={tab} index={3}>
                <SecretsList embedded />
            </TabPanel>
            <TabPanel value={tab} index={4}>
                <JwtKeyList embedded />
            </TabPanel>
            <TabPanel value={tab} index={5}>
                <OAuthClientList resource="admin/oauth-clients" />
            </TabPanel>
            <TabPanel value={tab} index={6}>
                <ApiConfigList embedded />
            </TabPanel>
            <TabPanel value={tab} index={7}>
                <SettingsPanel />
            </TabPanel>
            {TEST_USERS_ENABLED && (
                <TabPanel value={tab} index={8}>
                    <TestScenarioPanel />
                </TabPanel>
            )}
        </Box>
    );
};

export default HallintaDashboard;
